import { useEffect, useRef, useState } from "react";
import { OpenSheetMusicDisplay } from "opensheetmusicdisplay";
import QuestionLayout from "./QuestionLayout";
import {
  collectBarlineEntries,
  buildBarlineSlots,
} from "./drawBarlineGeometry";

const NS = "http://www.w3.org/2000/svg";
function svgElement(name, attributes) {
  const element = document.createElementNS(NS, name);
  for (const [key, value] of Object.entries(attributes))
    element.setAttribute(key, String(value));
  return element;
}
function drawLine(layer, slot, type, ghost = false) {
  const group = svgElement("g", { "pointer-events": "none" });
  for (const [dx, width] of type === "double"
    ? [
        [-2, 1.4],
        [3, 2.6],
      ]
    : [[0, 1.4]]) {
    group.appendChild(
      svgElement("line", {
        x1: slot.x + dx,
        x2: slot.x + dx,
        y1: slot.topY,
        y2: slot.bottomY,
        stroke: ghost ? "#999" : "#000",
        "stroke-width": width,
        ...(ghost ? { "stroke-dasharray": "4 3" } : {}),
      }),
    );
  }
  layer.appendChild(group);
  return group;
}
function hideOriginalBarlines(container, testEndBarline) {
  const rects = [...container.querySelectorAll("svg rect")].filter((rect) => {
    const box = rect.getBBox();
    return box.height >= 30 && box.width > 0 && box.width < 4;
  });
  // Keep the supplied final double barline when students are not asked to place it.
  const retained = testEndBarline ? 0 : Math.min(2, rects.length);
  rects.slice(0, rects.length - retained).forEach((rect) => {
    rect.style.visibility = "hidden";
  });
}

export default function DrawBarlinesWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback,
}) {
  const containerRef = useRef(null),
    osmdRef = useRef(null),
    slotsRef = useRef([]);
  const valueRef = useRef(value),
    modeRef = useRef(mode),
    onChangeRef = useRef(onChange),
    typeRef = useRef("single");
  const redrawRef = useRef(null);
  const [activeType, setActiveType] = useState("single"),
    [loadError, setLoadError] = useState("");
  valueRef.current = value;
  modeRef.current = mode;
  onChangeRef.current = onChange;
  typeRef.current = config.testEndBarline ? activeType : "single";

  function redraw() {
    const container = containerRef.current;
    if (!container) return;
    container
      .querySelectorAll(".draw-barline-layer")
      .forEach((layer) => layer.remove());
    const layers = new Map();
    for (const slot of slotsRef.current) {
      let layer = layers.get(slot.svg);
      if (!layer) {
        // Append to the SVG itself, not an engraving group with its own transform.
        layer = svgElement("g", { class: "draw-barline-layer" });
        slot.svg.appendChild(layer);
        layers.set(slot.svg, layer);
      }
      if (valueRef.current[slot.id])
        drawLine(layer, slot, valueRef.current[slot.id]);
      if (modeRef.current === "review") continue;
      const ghost = drawLine(layer, slot, typeRef.current, true);
      ghost.style.visibility = "hidden";
      const hit = svgElement("rect", {
        x: slot.x - 9,
        y: slot.topY,
        width: 18,
        height: slot.bottomY - slot.topY,
        fill: "transparent",
        "pointer-events": "all",
        "data-barline-slot": slot.id,
        tabindex: 0,
        role: "button",
        "aria-label": `Place or remove barline ${slot.id}`,
      });
      hit.style.cursor = "pointer";
      hit.style.touchAction = "manipulation";
      const toggle = (event) => {
        if (modeRef.current === "review") return;
        if (
          event.type === "pointerdown" &&
          event.pointerType === "mouse" &&
          event.button !== 0
        )
          return;
        event.preventDefault();
        event.stopPropagation();
        const next = { ...valueRef.current };
        if (next[slot.id]) delete next[slot.id];
        else next[slot.id] = typeRef.current;
        valueRef.current = next;
        ghost.style.visibility = "hidden";
        onChangeRef.current?.(next);
      };
      hit.addEventListener("pointerdown", toggle);
      hit.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") toggle(event);
      });
      hit.addEventListener("pointerenter", () => {
        if (window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches)
          ghost.style.visibility = "visible";
      });
      hit.addEventListener("pointerleave", () => {
        ghost.style.visibility = "hidden";
      });
      hit.addEventListener("focus", () => {
        ghost.style.visibility = "visible";
      });
      hit.addEventListener("blur", () => {
        ghost.style.visibility = "hidden";
      });
      layer.appendChild(hit);
    }
  }
  redrawRef.current = redraw;

  useEffect(() => {
    const container = containerRef.current;
    let cancelled = false,
      loaded = false,
      frame = 0;
    let observer = null;
    slotsRef.current = [];
    setLoadError("");
    container.innerHTML = "";
    const osmd = new OpenSheetMusicDisplay(container, {
      drawTitle: false,
      drawComposer: false,
      drawPartNames: false,
      drawMeasureNumbers: false,
      autoResize: false,
    });
    osmdRef.current = osmd;
    const render = () => {
      if (cancelled || !loaded) return;
      osmd.render();
      hideOriginalBarlines(container, config.testEndBarline);
      const entries = collectBarlineEntries(osmd);
      slotsRef.current = buildBarlineSlots(entries, config);
      if (!entries.length)
        throw new Error("Could not locate the score's rhythmic entries.");
      redrawRef.current?.();
    };
    (async () => {
      try {
        await osmd.load(config.musicXML);
        if (cancelled) return;
        loaded = true;
        render();
        let width = container.clientWidth;
        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(() => {
            const nextWidth = container.clientWidth;
            if (Math.abs(nextWidth - width) < 1) return;
            width = nextWidth;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              try {
                render();
              } catch (err) {
                if (!cancelled) setLoadError(err.message);
              }
            });
          });
          observer.observe(container);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Could not load score");
      }
    })();
    return () => {
      cancelled = true;
      observer?.disconnect();
      cancelAnimationFrame(frame);
      if (osmdRef.current === osmd) osmdRef.current = null;
    };
  }, [
    config.musicXML,
    config.scoreWidth,
    config.level,
    config.barlineSlotOffset,
    config.testEndBarline,
  ]);

  useEffect(() => {
    redrawRef.current?.();
  }, [value, mode, activeType]);
  const barOptions = config.testEndBarline ? ["single", "double"] : ["single"];
  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      {loadError && (
        <p role="alert">Unable to display this score: {loadError}</p>
      )}
      <div style={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
        <div
          ref={containerRef}
          style={{
            width: config.scoreWidth ? `${config.scoreWidth}px` : "100%",
            minWidth: config.scoreWidth ? `${config.scoreWidth}px` : undefined,
          }}
        />
      </div>
      {mode !== "review" && (
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          {barOptions.map((type) => (
            <button
              type="button"
              key={type}
              aria-pressed={typeRef.current === type}
              aria-label={
                type === "single" ? "Single barline" : "Double barline"
              }
              onClick={() => setActiveType(type)}
              style={{
                padding: "6px 12px",
                border:
                  typeRef.current === type
                    ? "2px solid #007aff"
                    : "1px solid #ccc",
                cursor: "pointer",
                fontWeight: "bold",
                color: "#000",
                background: "#fff",
              }}
            >
              {type === "single" ? "|" : "||"}
            </button>
          ))}
        </div>
      )}
    </QuestionLayout>
  );
}
